import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { baseurl } from '../Baseurl';
import axiosInstance from '../api/axiosInstance';

// Load initial business settings from localStorage
const loadInitialBusinessSettings = () => {
  try {
    const stored = localStorage.getItem('businessSetting');
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

const loadInitialAllBranchSettings = () => {
  try {
    const stored = localStorage.getItem('allBranchSettings');
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
};
export const fetchAllBranchSettings = createAsyncThunk(
  'business/fetchAllBranchSettings',
  async (doctorId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`getAllBranchSettings/${doctorId}`);
      const data = response.data?.data || [];
      if (data.length > 0) {
        const primary = { ...data[0], image: data[0].image || data[0].logo || null };
        localStorage.setItem('businessSetting', JSON.stringify(primary));
        if (primary.image) localStorage.setItem('businessSettingImage', primary.image);
        window.dispatchEvent(new CustomEvent('businessSettingUpdated', { detail: { businessSetting: primary } }));
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch branch settings');
    }
  }
);

export const updateBranchSetting = createAsyncThunk(
  'business/updateBranchSetting',
  async ({ branchId, formData, logoFile, doctorId, isPrimary }, { rejectWithValue, dispatch }) => {
    try {
      const payload = {
        official_name: formData.official_name,
        official_name_arabic: formData.official_name_arabic,
        cr_number: formData.cr_number,
        telephone_1: formData.telephone_1,
        telephone_2: formData.telephone_2,
        vat_number: formData.vat_number,
        email: formData.email,
        branches: isPrimary && formData.branches !== undefined ? (parseInt(formData.branches) || 1) : undefined,
        specialty: formData.specialty,
        address: formData.address,
        website: formData.website,
        dental_diagram_mode: formData.dental_diagram_mode || "universal",
        ...(formData.enabled_forms !== undefined && { enabled_forms: formData.enabled_forms }),
      };

      const endpoint = `${baseurl}updateBusinessSettingById/${branchId}`;
      let requestData = payload;
      let config = {};

      if (logoFile) {
        const fd = new FormData();
        Object.entries(payload).forEach(([k, v]) => { if (v !== undefined) fd.append(k, String(v ?? '')); });
        fd.append('image', logoFile);
        requestData = fd;
        config = { headers: { 'Content-Type': 'multipart/form-data' } };
      }

      await axiosInstance.put(endpoint, requestData, config);

      // Refresh all branch settings
      const freshResponse = await axiosInstance.get(`getAllBranchSettings/${doctorId}`);
      const freshData = freshResponse.data?.data || [];

      if (freshData.length > 0) {
        const primary = { ...freshData[0], image: freshData[0].image || freshData[0].logo || null };
        localStorage.setItem('businessSetting', JSON.stringify(primary));
        if (primary.image) localStorage.setItem('businessSettingImage', primary.image);
        window.dispatchEvent(new CustomEvent('businessSettingUpdated', { detail: { businessSetting: primary } }));
      }

      return freshData;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update branch setting');
    }
  }
);

export const fetchBusinessSettings = createAsyncThunk(
  'business/fetchBusinessSettings',
  async (doctorId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`getBusinessSettingByDoctorId/${doctorId}`);
      const data = response.data?.data || response.data;
      if (data) {
        const dataToStore = {
          ...data,
          image: data.image || data.logo || null
        };
        localStorage.setItem('businessSetting', JSON.stringify(dataToStore));
        if (data.image || data.logo) {
          localStorage.setItem('businessSettingImage', data.image || data.logo);
        }
        window.dispatchEvent(new CustomEvent('businessSettingUpdated', {
          detail: { businessSetting: dataToStore }
        }));
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch business settings');
    }
  }
);
export const updateBusinessSettings = createAsyncThunk(
  'business/updateBusinessSettings',
  async ({ doctorId, formData, businessSettingId, logoFile }, { rejectWithValue, dispatch }) => {
    try {
      const payload = {
        official_name: formData.official_name,
        official_name_arabic: formData.official_name_arabic,
        cr_number: formData.cr_number,
        telephone_1: formData.telephone_1,
        telephone_2: formData.telephone_2,
        vat_number: formData.vat_number,
        email: formData.email,
        branches: parseInt(formData.branches) || 0,
        specialty: formData.specialty,
        address: formData.address,
        website: formData.website,
        dental_diagram_mode: formData.dental_diagram_mode || "universal",
        ...(formData.enabled_forms !== undefined && { enabled_forms: formData.enabled_forms }),
      };
      const endpoint = businessSettingId
        ? `${baseurl}updateBusinessSetting/${businessSettingId}`
        : `${baseurl}updateBusinessSetting/${doctorId}`;
      let requestData = payload;
      let config = {};
      if (logoFile) {
        const formDataToSend = new FormData();
        formDataToSend.append('official_name', payload.official_name || '');
        formDataToSend.append('official_name_arabic', payload.official_name_arabic || '');
        formDataToSend.append('cr_number', payload.cr_number || '');
        formDataToSend.append('telephone_1', payload.telephone_1 || '');
        formDataToSend.append('telephone_2', payload.telephone_2 || '');
        formDataToSend.append('vat_number', payload.vat_number || '');
        formDataToSend.append('email', payload.email || '');
        formDataToSend.append('branches', String(payload.branches || 0));
        formDataToSend.append('specialty', payload.specialty || '');
        formDataToSend.append('dental_diagram_mode', payload.dental_diagram_mode || "universal");
        formDataToSend.append('image', logoFile);
        requestData = formDataToSend;
        config = { headers: { 'Content-Type': 'multipart/form-data' } };
      }
      const response = await axiosInstance.put(endpoint, requestData, config);
      const freshDataResponse = await axiosInstance.get(`getBusinessSettingByDoctorId/${doctorId}`);
      const freshData = freshDataResponse.data?.data || freshDataResponse.data;
      if (freshData) {
        const dataToStore = {
          ...freshData,
          image: freshData.image || freshData.logo || null
        };
        localStorage.setItem('businessSetting', JSON.stringify(dataToStore));
        if (freshData.image || freshData.logo) {
          localStorage.setItem('businessSettingImage', freshData.image || freshData.logo);
        }
        window.dispatchEvent(new CustomEvent('businessSettingUpdated', {
          detail: { businessSetting: dataToStore }
        }));
      }
      return freshData;
    } catch (error) {
      console.error('Update business settings error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to update business settings');
    }
  }
);

const loadActiveBranchId = () => {
  const stored = localStorage.getItem('activeBranchId');
  if (stored && stored !== 'undefined' && stored !== 'null') {
    const parsed = parseInt(stored);
    if (!isNaN(parsed)) return parsed;
  }
  try {
    const all = JSON.parse(localStorage.getItem('allBranchSettings') || '[]');
    if (all.length > 0 && all[0].id) {
      localStorage.setItem('activeBranchId', String(all[0].id));
      localStorage.setItem('activeBranchSettings', JSON.stringify(all[0]));
      return all[0].id;
    }
  } catch {}
  return null;
};

const loadActiveBranchSettings = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('activeBranchSettings') || 'null');
    if (stored) return stored;
    const all = JSON.parse(localStorage.getItem('allBranchSettings') || '[]');
    return all.length > 0 ? all[0] : null;
  } catch { return null; }
};

const businessSlice = createSlice({
  name: 'business',
  initialState: {
    businessSettings: loadInitialBusinessSettings(),
    allBranchSettings: loadInitialAllBranchSettings(),
    activeBranchId: loadActiveBranchId(),
    activeBranchSettings: loadActiveBranchSettings(),
    loading: false,
    error: null
  },
  reducers: {
    setActiveBranch(state, action) {
      state.activeBranchId = action.payload.id;
      state.activeBranchSettings = action.payload;
      localStorage.setItem('activeBranchId', String(action.payload.id));
      localStorage.setItem('activeBranchSettings', JSON.stringify(action.payload));
    },
    clearActiveBranch(state) {
      state.activeBranchId = null;
      state.activeBranchSettings = null;
      localStorage.removeItem('activeBranchId');
      localStorage.removeItem('activeBranchSettings');
    },
    setBusinessSettings(state, action) {
      state.businessSettings = action.payload;
      if (action.payload) {
        localStorage.setItem('businessSetting', JSON.stringify(action.payload));
        if (action.payload.image || action.payload.logo) {
          localStorage.setItem('businessSettingImage', action.payload.image || action.payload.logo);
        }
        window.dispatchEvent(new CustomEvent('businessSettingUpdated', {
          detail: { businessSetting: action.payload }
        }));
      }
    },
    clearBusinessSettings(state) {
      state.businessSettings = null;
      state.allBranchSettings = [];
      localStorage.removeItem('businessSetting');
      localStorage.removeItem('allBranchSettings');
    },
    updateOfficialName(state, action) {
      if (state.businessSettings) {
        state.businessSettings.official_name = action.payload;
        localStorage.setItem('businessSetting', JSON.stringify(state.businessSettings));
        if (state.businessSettings.image || state.businessSettings.logo) {
          localStorage.setItem('businessSettingImage', state.businessSettings.image || state.businessSettings.logo);
        }
      }
    },
    setAllBranchSettings(state, action) {
      state.allBranchSettings = action.payload;
      localStorage.setItem('allBranchSettings', JSON.stringify(action.payload));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBusinessSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBusinessSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.businessSettings = action.payload;
      })
      .addCase(fetchBusinessSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateBusinessSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBusinessSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.businessSettings = action.payload;
      })
      .addCase(updateBusinessSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllBranchSettings.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAllBranchSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.allBranchSettings = action.payload;
        localStorage.setItem('allBranchSettings', JSON.stringify(action.payload));
        if (action.payload.length > 0) state.businessSettings = action.payload[0];
      })
      .addCase(fetchAllBranchSettings.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(updateBranchSetting.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(updateBranchSetting.fulfilled, (state, action) => {
        state.loading = false;
        state.allBranchSettings = action.payload;
        localStorage.setItem('allBranchSettings', JSON.stringify(action.payload));
        if (action.payload.length > 0) state.businessSettings = action.payload[0];
      })
      .addCase(updateBranchSetting.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
  }
});
export const { setBusinessSettings, clearBusinessSettings, updateOfficialName, setActiveBranch, clearActiveBranch, setAllBranchSettings } = businessSlice.actions;
export default businessSlice.reducer;
